import { Filter, FilterOperation, QueryParameters, Sort, SortDirection } from "../models/queryParameters";

export interface PageSearchParams {
    params: { slug: string }
    searchParams: { [key: string]: string | string[] | undefined }
}

export const mapPageSearchParamsToRequestOptions = (searchParams: { [key: string]: string | string[] | undefined }): QueryParameters => {
    const requestOptions: QueryParameters = {};

    // Parse basic parameters
    if (searchParams['page']) {
        requestOptions.page = parseInt(searchParams['page'] as string, 10) || 1;
    }

    if (searchParams['pageSize']) {
        requestOptions.pageSize = parseInt(searchParams['pageSize'] as string, 10) || 10;
    }

    if (searchParams['searchTerm']) {
        requestOptions.searchTerm = searchParams['searchTerm'] as string;
    }

    // Parse filters
    if (searchParams['filter']) {
        const filterParams = JSON.parse(searchParams['filter'] as string);

        if (Array.isArray(filterParams)) {
            requestOptions.filter = filterParams.map(filter => ({
                property: filter.property,
                value: filter.value,
                operation: filter.operation
            }));
        }
    }

    // Parse sorts
    if (searchParams['sort']) {
        const sortParams = JSON.parse(searchParams['sort'] as string);
        if (Array.isArray(sortParams)) {
            requestOptions.sort = sortParams.map(sort => ({
                property: sort.property,
                order: sort.order
            }));
        }
    }

    return requestOptions;
}


export const mapParamsToRequestOptions = (searchParams: URLSearchParams): QueryParameters => {
    const requestOptions: QueryParameters = {};

    // Parse basic parameters
    if (searchParams.has('page')) {
        requestOptions.page = parseInt(searchParams.get('page')!, 10) || 1;
    }

    if (searchParams.has('pageSize')) {
        requestOptions.pageSize = parseInt(searchParams.get('pageSize')!, 10) || 10;
    }

    if (searchParams.has('searchTerm')) {
        requestOptions.searchTerm = searchParams.get('searchTerm')!;
    }

    // Parse filters
    if (searchParams.has('filter')) {
        const filterParams = JSON.parse(searchParams.get('filter')!);
        if (Array.isArray(filterParams)) {
            requestOptions.filter = filterParams.map(filter => ({
                property: filter.property,
                value: filter.value,
                operation: filter.operation
            }));
        }
    }

    // Parse sorts
    if (searchParams.has('sort')) {
        const sortParams = JSON.parse(searchParams.get('sort')!);
        if (Array.isArray(sortParams)) {
            requestOptions.sort = sortParams.map(sort => ({
                property: sort.property,
                order: sort.order
            }));
        }
    }

    return requestOptions;
}

export const mapRequestOptionsToParams = (requestOptions: QueryParameters): URLSearchParams => {

    const params = new URLSearchParams();

    if (requestOptions.page !== undefined) {
        params.set('page', requestOptions.page.toString());
    }
    else {
        params.delete('page');
    }

    if (requestOptions.pageSize !== undefined) {
        params.set('pageSize', requestOptions.pageSize.toString());
    }
    else {
        params.delete('pageSize');
    }

    if (requestOptions.searchTerm !== undefined) {
        params.set('searchTerm', requestOptions.searchTerm);
    }
    else {
        params.delete('searchTerm');
    }

    if (requestOptions.filter && requestOptions.filter.length > 0) {
        params.set('filter', JSON.stringify(requestOptions.filter));
    }
    else {
        params.delete('filter');
    }

    if (requestOptions.sort && requestOptions.sort.length > 0) {
        params.set('sort', JSON.stringify(requestOptions.sort));
    }
    else {
        params.delete('sort');
    }

    return params;
};

export const addOrUpdateFilter = (requestOptions: QueryParameters, property: string, value: string, operation: FilterOperation): QueryParameters => {

    const filters: Filter[] = requestOptions.filter || [];

    const filterIndex = filters.findIndex(filter => filter.property === property);

    if (value) {
        if (filterIndex >= 0) {
            filters[filterIndex].value = value;
        } else {
            filters.push({ property: property, value: value, operation: operation });
        }
    } else if (filterIndex >= 0) {
        filters.splice(filterIndex, 1);
    }

    requestOptions.filter = filters;
    return requestOptions;
}

export const getSearchParamsFilter = (searchParams: URLSearchParams, property: string): string | undefined => {

    if (searchParams.has('filter')) {
        const filters = JSON.parse(searchParams.get('filter')!) as Filter[];
        if (Array.isArray(filters)) {
            const filter = filters.find(f => f.property == property);
            return filter?.value;
        }
    }
}

export const addOrUpdateSort = (requestOptions: QueryParameters, property: string, direction: SortDirection): QueryParameters => {

    const sorts: Sort[] = requestOptions.sort || [];

    const sortIndex = sorts.findIndex(sort => sort.property === property);

    if (sortIndex >= 0) {
        sorts[sortIndex].order = direction;
    } else {
        sorts.push({ property: property, order: direction });
    }

    requestOptions.sort = sorts;

    return requestOptions;
}

export const removeSort = (requestOptions: QueryParameters, property: string): QueryParameters => {

    const sorts: Sort[] = requestOptions.sort || [];

    requestOptions.sort = sorts.filter(s => s.property != property);

    return requestOptions;
}

export const isSortEmpty = (requestOptions: QueryParameters): boolean => {
    return !requestOptions || !requestOptions.sort || requestOptions.sort.length === 0;
}
